import React, { useEffect } from 'react';
import * as PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Form from '../../form/components/Form';
import { IdPropType } from '../../../proptypes/basic';
import SubmitButton from '../../form/components/SubmitButton';
import TransferStatusSelect, {
    TRANSFER_STATUS_OPTIONS,
} from '../../form/components/specialized/TransferStatusSelect';
import DateInput from '../../form/components/DateInput';
import TextInput from '../../form/components/TextInput';
import transportationChoices from '../../../config/nurseTransferTransportationMeans';
import Select from '../../form/components/Select';
import Checkbox from '../../form/components/Checkbox';
import TransferDestinationSelect from '../../form/components/specialized/TransferDestinationSelect';
import ConditionalField from '../../form/components/specialized/ConditionalField';
import TransferPickupSelect from '../../form/components/specialized/TransferPickupSelect';
import PhoneNumberInput from '../../form/components/specialized/PhoneNumberInput';
import { Alert } from '@material-ui/lab';
import { transferFormSchema } from '../schema';
import {
    selectNurseTransferById,
    storeNurseTransfer,
    updateNurseTransfer,
} from '../nurseTransfersSlice';
import { NURSE_TRANSFER_RESOURCE } from '../../api/resources';
import { 
        updateNurseDeployment,
        selectNurseDeploymentById
 } from '../../nurseDeployments/nurseDeploymentsSlice';
import { useInitialValues, useFieldFast } from '../../form/hooks';
import { getEmptyPhoneNumber } from '../../form/components/specialized/PhoneNumbersInput';
import { selectUserIsRoot, selectUserIsBuHa } from '../../../Modules/auth/selectors';
import { isCommissionDataFixated } from '../../../helpers/tools';
import {
    useNurseDeploymentsByCustomerContractId
} from '../../nurseDeployments/nurseDeploymentsSlice';
import { selectAllNurseDeploymentFixations, indexNurseDeploymentFixations } from '../../nurseDeploymentFixation/nurseDeploymentFixationSlice';

const useStyles = makeStyles((theme) => ({
    header: {
        marginTop: theme.spacing(2),
    },
}));

// no date to give yet for these statuses, so hide the date input for them
const STATUSES_WITHOUT_DATE = ['details_agency_requested', 'waiting_for_customer', 'details_complete'];


// needs to live inside the Formik context (i.e. inside <Form>), so it's a separate component rather than inline logic in TransferForm
const TransferStatusSelectField = ({ disabled }) => {
    const [{ value: status }] = useFieldFast('status');

    return (
        <TransferStatusSelect
            name="status"
            label="Status"
            withDate={!STATUSES_WITHOUT_DATE.includes(status)}
            disabled={disabled}
        />
    );
};

TransferStatusSelectField.propTypes = {
    disabled: PropTypes.bool,
};

TransferStatusSelectField.defaultProps = {
    disabled: false,
};

const TransferForm = ({ arrivalTransferId, departureTransferId, deploymentId, customerId, type, submitContainer, onDone}) => {
    const transferId = type === 'arrival' ? arrivalTransferId : departureTransferId;
    const classes = useStyles();
    const transfer = useSelector((state) => selectNurseTransferById(state, transferId));
    const transferArrival  = useSelector((state) => selectNurseTransferById(state, arrivalTransferId));
    const transferDeparture  = useSelector((state) => selectNurseTransferById(state, departureTransferId));
    const deployment = useSelector((state) => selectNurseDeploymentById(state, deploymentId));
    const dispatch = useDispatch();
    const isRoot = useSelector(selectUserIsRoot);
    const isBuHa = useSelector(selectUserIsBuHa);

    // update the fixation dates so we can work with them
    const dispatch_nurseDeploymentFixations = useDispatch();
    useEffect(() => {
        dispatch_nurseDeploymentFixations(indexNurseDeploymentFixations());
    }, [dispatch_nurseDeploymentFixations]);

    // get all nurse deployment fixations
    const nurseDeploymentFixations = useSelector(selectAllNurseDeploymentFixations);
    // now find the latest fixation date
    const latestFixation = nurseDeploymentFixations.reduce((latest, current) => {
        const currentDate = new Date(current.fixedAt);
        const latestDate = latest ? new Date(latest.fixedAt) : null;
        return currentDate > latestDate ? current : latest;
    }, null);

    const [allNurseDeploymentsByCustomerContractId] = useNurseDeploymentsByCustomerContractId(deployment?.customerContractId);
    //console.log("TransferForm  => ",deployment);
 
    //console.log(`deployment => ${transferArrival?.date} => ${transferDeparture?.date}`);
    const transferFormSchemaByType = transferFormSchema(type, transferArrival?.date, transferDeparture?.date, allNurseDeploymentsByCustomerContractId, deployment);

   // check with the billingFrom date if the data is fixated
    const today = new Date();
    //console.log("check with transfer.date =>",transfer?.date, "today => ", today," latestFixation =>" , latestFixation?.fixedAt);
    //let is_fixated = isCommissionDataFixated(transfer?.date, today, latestFixation);

    // check if the transfer is fixated by the deployment billingFrom date
    let is_fixated = isCommissionDataFixated(type === 'arrival' ? deployment?.billingFrom : deployment?.billingTill, today, latestFixation);
    //console.log("type => ", type, " is_fixated => ", is_fixated, " deployment.billingFrom => ", deployment?.billingFrom, " deployment.billingTill => ", deployment?.billingTill, " latestFixation => ", latestFixation?.fixedAt);
    let is_fixation_overridden = false;


    if (is_fixated)
    {
        // we still can if we're Root or BuHa
        if (isRoot || isBuHa)
        {
            console.log("disabled is_fixated for Root or BuHa");
            is_fixated = false;
            is_fixation_overridden = true; // we can override the fixation
        }
    }
    //console.log("is_fixated => ",is_fixated," fixedAt => ", latestFixation?.fixedAt);

    const handleSubmit = async (values) => {
        const prepped = {
            id: transferId,
            ...values,
            type,
        };

        /* is there a better way to do this? */
        TRANSFER_STATUS_OPTIONS.forEach(({ value: status }) => {
            if (values.status !== status) {
                delete prepped[`${status}_date`];
            }
        });

        if (values.destination === 'patient_household') {
            delete prepped.destinationName;
            delete prepped.destinationAddress;
            delete prepped.destinationZip;
            delete prepped.destinationCity;
        }

        if (!values.transferRequired) {
            delete prepped.pickup;
            delete prepped.pickupDetails;
            delete prepped.phonenumberName;
            delete prepped.phoneNumbers;
        }

        if (
            values.transferRequired &&
            prepped.phoneNumbers &&
            prepped.phoneNumbers[0].type.length === 0 &&
            prepped.phoneNumbers[0].prefix.length === 0 &&
            prepped.phoneNumbers[0].number.length === 0
        ) {
            delete prepped.phoneNumbers;
        }

        const response = await dispatch(
            transferId
                ? updateNurseTransfer(prepped)
                : storeNurseTransfer(prepped, { deploymentId })
        );

        if (!transferId) {
            const billtype = type === 'arrival' ? 'From' : 'Till';
            const deployprep = {
                id: deploymentId,
                [`${type}TransferId`]: response.data.id,
            }
            if (prepped.date !== null) deployprep[`billing${billtype}`] = prepped.date;
            await dispatch(updateNurseDeployment(deployprep));
        } else {
            if (prepped.date !== null) {
                const billtype = type === 'arrival' ? 'From' : 'Till';
                await dispatch(
                    updateNurseDeployment({
                        id: deploymentId,
                        [`billing${billtype}`]: prepped.date,
                    })
                );
            }
        }
        onDone(response);
    };

    const initialValues = useInitialValues(transfer, {
        status: '',
        detailsAgencyReceivedDate: null,
        detailsAgencyRequestedDate: null,
        detailsCompleteDate: null,
        detailsDoneDate: null,
        detailsSentAgencyDate: null,
        detailsSentCustomerDate: null,
        waitingForCustomerDate: null,
        date: null,
        time: '',
        costSharing: '',
        meansOfTravel: '',
        meansOfTravelDetails: '',
        transferRequired: false,
        destination: '',
        destinationName: '',
        destinationAddress: '',
        destinationZip: '',
        destinationCity: '',

        pickup: '',
        pickupDetails: '',
        phoneNumberName: '',
        phoneNumbers: (value) =>
            value && value.phoneNumbers && value.phoneNumbers.length === 1
                ? value.phoneNumbers
                : [getEmptyPhoneNumber()],
    });

    return (
        <Form
            initialValues={initialValues}
            validationSchema={transferFormSchemaByType}
            onSubmit={handleSubmit}
            subject={transfer || NURSE_TRANSFER_RESOURCE}
        >
            <Grid container spacing={2}>
                {is_fixated && (
                    <Grid item xs={8}>
                        <Alert severity="info">
                            Diese Daten sind gesperrt{latestFixation && latestFixation.fixationMonth ? ` (Abrechnung ${new Date(latestFixation.fixationMonth).toLocaleString('de-DE', { month: 'long', year: 'numeric' })})` : ''}, für Änderungen bitte an die BuHa wenden!
                        </Alert>
                    </Grid>
                )}
                {is_fixation_overridden && (
                    <Grid item xs={8}>
                        <Alert severity="info">
                            Diese Daten sind gesperrt{latestFixation && latestFixation.fixationMonth ? ` (fixiert bis ${new Date(latestFixation.fixationMonth).toLocaleString('de-DE', { month: 'long', year: 'numeric' })})` : ''}, aber Sie können sie ändern, da Sie Root oder BuHa sind!
                        </Alert>
                    </Grid>
                )}
                <Grid item xs={8}>
                    <TransferStatusSelectField disabled={is_fixated} />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h3" className={classes.header}>
                        Details
                    </Typography>
                </Grid>

                <Grid item xs={4}>
                    <DateInput 
                        name="date" 
                        label="Datum" 
                        fullWidth 
                        disabled={is_fixated}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextInput
                        name="time"
                        label="Uhrzeit"
                        fullWidth
                        maxLength={255}
                        maxLengthHidden
                        disabled={is_fixated}
                    />
                </Grid>

                <Grid item xs={4} />

                <Grid item xs={8}>
                    <TextInput
                        name="costSharing"
                        label="Kostenbeteiligung Transport"
                        maxLength={50}
                        fullWidth
                        disabled={is_fixated}
                    />
                </Grid>

                <Grid item xs={4} />

                <Grid item xs={4}>
                    <Select
                        name="meansOfTravel"
                        label="Verkehrsmittel"
                        options={[
                            { value: '', label: 'Verkehrsmittel auswählen' },
                            ...transportationChoices.map(({ id, label }) => ({
                                value: id,
                                label,
                            })),
                        ]}
                        fullWidth
                        disabled={is_fixated}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextInput
                        name="meansOfTravelDetails"
                        label="Details"
                        fullWidth
                        maxLength={255}
                        disabled={is_fixated}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Checkbox 
                        name="transferRequired"
                        label="Transfer erforderlich"
                        disabled={is_fixated}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TransferDestinationSelect
                        name="destination"
                        nameName="destinationName"
                        nameAddress="destinationAddress"
                        nameZip="destinationZip"
                        nameCity="destinationCity"
                        label="Ort"
                        customerId={customerId}
                        spacing={2}
                        disabled={is_fixated}
                    />
                </Grid>

                <ConditionalField conditionFieldName="transferRequired">
                    <Grid item xs={12}>
                        <Typography variant="h3" className={classes.header}>
                            Transfer
                        </Typography>
                    </Grid>

                    <Grid item xs={4}>
                        <TransferPickupSelect name="pickup" label="Transfer" fullWidth />
                    </Grid>

                    <Grid item xs={8}>
                        <TextInput
                            name="pickupDetails"
                            label="Details Transfer"
                            maxLength={255}
                            rows={3}
                            rowsMax={10}
                            multiline
                            fullWidth
                            disabled={is_fixated}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography className={classes.subheader}>Telefonnummer</Typography>
                    </Grid>

                    <Grid item xs={4}>
                        <TextInput 
                            name="phoneNumberName" 
                            label="Name" 
                            maxLength={255} 
                            fullWidth 
                            disabled={is_fixated}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <PhoneNumberInput 
                            name="phoneNumbers.0" 
                            preview 
                            selectType 
                            spacing={2} 
                            disabled={is_fixated}
                        />
                    </Grid>
                </ConditionalField>
            </Grid>

            <SubmitButton 
                container={submitContainer} 
                disabled={is_fixated}/>
        </Form>
    );
};

TransferForm.propTypes = {
    transferId: IdPropType,
    deploymentId: IdPropType.isRequired,
    customerId: IdPropType.isRequired,
    type: PropTypes.string.isRequired,
    submitContainer: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]),
    onDone: PropTypes.func,
};

TransferForm.defaultProps = {
    transferId: null,
    submitContainer: null,
    onDone: () => null,
};

export default TransferForm;
